import React, { useState, useMemo } from 'react';
import { usePDF } from 'react-to-pdf';
import { Button } from './ui/button';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from './ui/table';
import { FileDown, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const STATS = ['goals', 'assists'];

function StatsSummary({ players, games, seasonName }) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'descending' });
  const pdfFilename = seasonName ? `stats_summary_${seasonName.toLowerCase().replace(/\s+/g, '_')}.pdf` : 'stats_summary.pdf';
  const { toPDF, targetRef } = usePDF({ filename: pdfFilename });

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return {
          key,
          direction: prev.direction === 'descending' ? 'ascending' : 'descending'
        };
      }
      return {
        key,
        direction: key === 'name' ? 'ascending' : 'descending'
      };
    });
  };

  const processedPlayers = useMemo(() => {
    if (!players || players.length === 0) return [];

    const calculateTotalStats = (playerId) => {
      if (!games || games.length === 0) {
        return { goals: 0, assists: 0 };
      }
      return games.reduce((totalStats, game) => {
        const playerStats = game.playerStats ? game.playerStats.find(ps => ps.player_id === playerId) : null;
        if (playerStats) {
          STATS.forEach(stat => {
            totalStats[stat] += playerStats[stat] || 0;
          });
        }
        return totalStats;
      }, { goals: 0, assists: 0 });
    };

    const list = players.map(player => ({
      ...player,
      totalStats: calculateTotalStats(player.id)
    }));

    if (sortConfig.key) {
      list.sort((a, b) => {
        if (sortConfig.key === 'name') {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          if (nameA < nameB) return sortConfig.direction === 'ascending' ? -1 : 1;
          if (nameA > nameB) return sortConfig.direction === 'ascending' ? 1 : -1;
          return 0;
        } else {
          const valA = a.totalStats[sortConfig.key] || 0;
          const valB = b.totalStats[sortConfig.key] || 0;
          if (valA < valB) return sortConfig.direction === 'ascending' ? -1 : 1;
          if (valA > valB) return sortConfig.direction === 'ascending' ? 1 : -1;
          return (a.name || '').localeCompare(b.name || '');
        }
      });
    }

    return list;
  }, [players, games, sortConfig]);

  const totalTeamGoals = useMemo(() => {
    if (!games || games.length === 0) return 0;
    return games.reduce((acc, game) => {
      const gameGoals = (game.playerStats || []).reduce((sum, ps) => sum + (ps.goals || 0), 0);
      return acc + gameGoals;
    }, 0);
  }, [games]);

  const totalOpponentGoals = useMemo(() => {
    if (!games || games.length === 0) return 0;
    return games.reduce((acc, game) => acc + (game.opponent_score || 0), 0);
  }, [games]);

  const goalDifferential = totalTeamGoals - totalOpponentGoals;

  return (
    <div className="space-y-4 pt-2">
      <div className="flex items-center justify-between pb-1">
        <span className="text-xs text-muted-foreground font-medium">
          {players ? players.length : 0} {players && players.length === 1 ? 'Player' : 'Players'}
          {sortConfig.key && (
            <span className="ml-2 text-primary font-normal">
              (Sorted by {sortConfig.key === 'name' ? 'name' : sortConfig.key} {sortConfig.direction === 'descending' ? '↓' : '↑'})
            </span>
          )}
        </span>
        <Button variant="outline" size="sm" onClick={toPDF} className="flex items-center gap-1.5 h-8 text-xs">
          <FileDown className="w-3.5 h-3.5 text-emerald-500" />
          <span>Export Summary PDF</span>
        </Button>
      </div>

      <div ref={targetRef} className="space-y-4">
        {/* 1. Player Stats Table */}
        <div className="rounded-md border overflow-hidden w-full">
          <Table className="table-fixed w-full">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead 
                  className="font-semibold px-3 py-2.5 cursor-pointer select-none hover:bg-muted/80 transition-colors"
                  onClick={() => handleSort('name')}
                  title="Click to sort by player name"
                >
                  <div className="inline-flex items-center gap-1.5">
                    <span>Player Name</span>
                    {sortConfig.key === 'name' ? (
                      sortConfig.direction === 'descending' ? (
                        <ArrowDown className="w-3.5 h-3.5 text-primary shrink-0" />
                      ) : (
                        <ArrowUp className="w-3.5 h-3.5 text-primary shrink-0" />
                      )
                    ) : (
                      <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
                    )}
                  </div>
                </TableHead>

                {STATS.map(stat => {
                  const isSorted = sortConfig.key === stat;
                  return (
                    <TableHead 
                      key={stat} 
                      className="text-center font-semibold capitalize w-24 sm:w-28 px-2 py-2.5 cursor-pointer select-none hover:bg-muted/80 transition-colors"
                      onClick={() => handleSort(stat)}
                      title={`Click to sort by ${stat}`}
                    >
                      <div className="inline-flex items-center justify-center gap-1.5 w-full">
                        <span>{stat}</span>
                        {isSorted ? (
                          sortConfig.direction === 'descending' ? (
                            <ArrowDown className="w-3.5 h-3.5 text-primary shrink-0" />
                          ) : (
                            <ArrowUp className="w-3.5 h-3.5 text-primary shrink-0" />
                          )
                        ) : (
                          <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground/40 shrink-0" />
                        )}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {processedPlayers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={STATS.length + 1} className="text-center text-muted-foreground py-6">
                    No players found in active roster.
                  </TableCell>
                </TableRow>
              ) : (
                processedPlayers.map(player => (
                  <TableRow key={player.id}>
                    <TableCell className="font-medium px-3 py-2.5 overflow-hidden">
                      <div className="truncate text-sm font-medium pr-1" title={player.name}>
                        {player.name}
                      </div>
                    </TableCell>
                    {STATS.map(stat => (
                      <TableCell key={stat} className="text-center font-semibold w-24 sm:w-28 px-2 py-2.5">
                        {player.totalStats[stat]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* 2. Total Goals For vs Against Table */}
        <div className="space-y-2 pt-1">
          <div className="flex items-center justify-between px-1">
            <h4 className="text-sm font-bold text-foreground">
              Total Goals
            </h4>
            <span className="text-xs text-muted-foreground">
              {games ? games.length : 0} {games && games.length === 1 ? 'Game' : 'Games'}
            </span>
          </div>

          <div className="rounded-md border overflow-hidden w-full">
            <Table className="table-fixed w-full">
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold px-3 py-2.5">Category</TableHead>
                  <TableHead className="text-center font-semibold px-3 py-2.5 w-28 sm:w-36">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="font-medium px-3 py-2.5">
                    Our Team (Goals For)
                  </TableCell>
                  <TableCell className="text-center font-bold text-sm sm:text-base px-3 py-2.5 text-foreground">
                    {totalTeamGoals}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-medium px-3 py-2.5">
                    Opponents (Goals Against)
                  </TableCell>
                  <TableCell className="text-center font-bold text-sm sm:text-base px-3 py-2.5 text-foreground">
                    {totalOpponentGoals}
                  </TableCell>
                </TableRow>
                <TableRow className="bg-muted/30 border-t">
                  <TableCell className="font-semibold px-3 py-2.5 text-foreground">
                    Goal Difference
                  </TableCell>
                  <TableCell className="text-center font-bold text-sm sm:text-base px-3 py-2.5">
                    <span className={
                      goalDifferential > 0 
                        ? 'text-emerald-600 dark:text-emerald-400' 
                        : (goalDifferential < 0 
                            ? 'text-rose-600 dark:text-rose-400' 
                            : 'text-muted-foreground')
                    }>
                      {goalDifferential > 0 ? `+${goalDifferential}` : goalDifferential}
                    </span>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StatsSummary;
